import * as React from "react";
import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ArrowLeft24Regular, DocumentAdd24Regular, Save24Regular } from "@fluentui/react-icons";
import { ApiError, getDoctype, updateDoctypeDocxFile } from "../../zegal/api";
import { Settings } from "../../zegal/settings";
import { Doctype, DoctypeListItem } from "../../zegal/types";
import { sfdtToHtml } from "../../zegal/sfdtToHtml";
import { getDocumentAsBase64Docx, insertDocxBase64, replaceBodyWithHtml } from "../../zegal/word";

interface TemplateDetailProps {
  settings: Settings;
  item: DoctypeListItem;
  onBack: () => void;
  onUnauthorized: () => void;
}

type Phase = "loading" | "ready" | "inserting" | "saving" | "error";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  title: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  buttonRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
});

function docxToBase64Source(docxFile: Doctype["docxFile"]): string | null {
  if (!docxFile) return null;
  if (typeof docxFile === "string") return docxFile;
  if (typeof docxFile === "object" && docxFile.url) return docxFile.url;
  return null;
}

async function fetchDocxAsBase64(source: string): Promise<string> {
  const response = await fetch(source, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Failed to fetch DOCX (${response.status})`);
  }
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

const TemplateDetail: React.FC<TemplateDetailProps> = ({ settings, item, onBack, onUnauthorized }) => {
  const styles = useStyles();
  const [doctype, setDoctype] = React.useState<Doctype | null>(null);
  const [phase, setPhase] = React.useState<Phase>("loading");
  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase("loading");
      setError(null);
      try {
        const result = await getDoctype(settings, item._id);
        if (cancelled) return;
        setDoctype(result);
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          onUnauthorized();
          return;
        }
        const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : (e as Error).message;
        setError(msg);
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settings, item._id]);

  const handleInsert = async () => {
    if (!doctype) return;
    setPhase("inserting");
    setError(null);
    setInfo(null);
    try {
      const docxSource = docxToBase64Source(doctype.docxFile);
      if (docxSource && /^https?:/i.test(docxSource)) {
        const base64 = await fetchDocxAsBase64(docxSource);
        await insertDocxBase64(base64);
        setInfo("Inserted template as DOCX.");
      } else if (docxSource && /^data:.*?;base64,/i.test(docxSource)) {
        const base64 = docxSource.slice(docxSource.indexOf(",") + 1);
        await insertDocxBase64(base64);
        setInfo("Inserted template as DOCX.");
      } else if (docxSource && /^[A-Za-z0-9+/=]+$/.test(docxSource) && docxSource.length > 100) {
        await insertDocxBase64(docxSource);
        setInfo("Inserted template as DOCX.");
      } else {
        const html = sfdtToHtml(doctype.sfdt);
        if (html.length > 300_000) {
          throw new Error(
            "This template is too large to insert via HTML. Ask a Zegal admin to attach a DOCX file to this template so it can be inserted directly."
          );
        }
        await replaceBodyWithHtml(html);
        setInfo(
          "Inserted template as HTML (basic formatting). Tables, images and lists are not preserved in this phase."
        );
      }
      setPhase("ready");
    } catch (e) {
      setError((e as Error).message);
      setPhase("error");
    }
  };

  const handleSave = async () => {

    if (!doctype) return;
    setPhase("saving");
    setError(null);
    setInfo(null);
    try {
      const docxBase64 = await getDocumentAsBase64Docx();
      await updateDoctypeDocxFile(settings, doctype._id, docxBase64);
      setInfo("Template content saved to Zegal.");
      setPhase("ready");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        onUnauthorized();
        return;
      }
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : (e as Error).message;
      setError(msg);
      setPhase("error");
    }
  };

  const busy = phase === "loading" || phase === "inserting" || phase === "saving";

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          onClick={onBack}
          aria-label="Back"
        />
        <div>
          <div className={styles.title}>{item.title || "Untitled template"}</div>
          {item.jurisdictions && item.jurisdictions.length > 0 && (
            <div className={styles.meta}>{item.jurisdictions.join(", ")}</div>
          )}
        </div>
      </div>

      {phase === "loading" && <Spinner labelPosition="after" label="Loading template..." />}

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {info && !error && (
        <MessageBar intent="success">
          <MessageBarBody>{info}</MessageBarBody>
        </MessageBar>
      )}

      {doctype && (
        <>
          <div className={styles.buttonRow}>
            <Button
              appearance="primary"
              icon={<DocumentAdd24Regular />}
              disabled={busy}
              onClick={handleInsert}
            >
              {phase === "inserting" ? "Inserting..." : "Insert into document"}
            </Button>
            <Button
              icon={<Save24Regular />}
              disabled={busy}
              onClick={handleSave}
            >
              {phase === "saving" ? "Saving..." : "Save to Zegal"}
            </Button>
          </div>

          <div className={styles.meta}>
            Fields: {doctype.fields?.length ?? 0}. Save overwrites this template's docxFile
            (PUT /doctypes/v3/{doctype._id}/docxFile).
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateDetail;
